<template>
  <div class="date-picker">
    <VueDatePicker
      :key="datePickerKey"
      v-model="selectedDate"
      :clearable="false"
      :format="formatDatePreview"
      :preview-format="dateFormat"
      locale="de-DE"
      :locale-format="de"
      :month-picker="props.timespan === 'month'"
      :year-range="yearRange"
      :reverse-years="true"
      :max-date="maxDate"
      :min-date="minDate"
      :enable-time-picker="false"
      :prevent-min-max-navigation="true"
      :auto-apply="true"
      now-button-label="Heute"
    />
  </div>
</template>

<script setup>
  /*
   * A custom date picker component that functions as light abstraction to the
   * the library / dependency being used. This way swapping out the library can
   * be done just in this file if necessary.
   */
  import VueDatePicker from '@vuepic/vue-datepicker'
  import '@vuepic/vue-datepicker/dist/main.css'
  import { computed, ref, watch } from 'vue'
  import { useDate } from '@/composables/useDate.ts'
  import { de } from 'date-fns/locale'
  import { format } from 'date-fns'

  // When setting a date format as string, localization for the preview does not
  // work for some reason. Working around this issue by using a custom formatter.
  const formatDatePreview = (date) => {
    if (!date) {
      return ''
    }
    return format(date, dateFormat.value, { locale: de })
  };

  const { TIMESPAN, formatDate } = useDate()

  const datePickerKey = ref(0)

  const props = defineProps({
    date: {
      type: [Date, Object],
      default: new Date(),
    },
    minDate: {
      type: Date,
      default: new Date('2018-01-01'),
    },
    maxDate: {
      type: Date,
      default: new Date(),
    },
    timespan: {
      type: String,
      default: 'day',
    },
  })

  const emit = defineEmits(['dateChanged'])
  const timespan = ref(props.timespan)
  watch(() => props.timespan, (newValue) => { timespan.value = newValue })

  const minDate = ref(props.minDate)
  const maxDate = ref(props.maxDate)
  const yearRange = computed(() => {
    return [minDate.value.getFullYear(), maxDate.value.getFullYear()]
  })

  const selectedDate = ref(props.date)
  watch(() => props.date, (newDate) => {
    selectedDate.value = newDate
  })
  watch(selectedDate, (newValue, oldValue) => {
    emit('dateChanged', newValue, oldValue)
  })

  const dateFormat = computed(() => {
    switch (timespan.value) {
      case TIMESPAN.MONTH:
        return 'MMMM yyyy'

      case TIMESPAN.DAY:
        return 'dd.MM.yyyy'

      default:
        throw new Error('Unknown timespan for date picker format: ' + timespan.value)
    }
  })
</script>

<style lang="scss">
  .date-picker {
    max-width: 12rem;
  }

  .dp__theme_light {
    --dp-font-family: #{$font-family-base};
    --dp-background-color: #fff;
    --dp-text-color: #212121;
    --dp-hover-color: #f3f3f3;
    --dp-hover-text-color: #212121;
    --dp-hover-icon-color: #959595;
    --dp-primary-color: #{$community};
    --dp-primary-disabled-color: #6bacea;
    --dp-primary-text-color: #{$body-color};
    --dp-secondary-color: #c0c4cc;
    --dp-border-color: #ddd;
    --dp-menu-border-color: #ddd;
    --dp-border-color-hover: #aaaeb7;
    --dp-border-color-focus: #aaaeb7;
    --dp-disabled-color: #f6f6f6;
    --dp-scroll-bar-background: #f3f3f3;
    --dp-scroll-bar-color: #959595;
    --dp-success-color: #76d275;
    --dp-success-color-disabled: #a3d9b1;
    --dp-icon-color: #959595;
    --dp-danger-color: #ff6f60;
    --dp-marker-color: #ff6f60;
    --dp-tooltip-color: #fafafa;
    --dp-disabled-color-text: #8e8e8e;
    --dp-highlight-color: rgb(25 118 210 / 10%);
    --dp-range-between-dates-background-color: var(--dp-hover-color, #f3f3f3);
    --dp-range-between-dates-text-color: var(--dp-hover-text-color, #212121);
    --dp-range-between-border-color: var(--dp-hover-color, #f3f3f3);
  }
</style>